// Globals
import { FC, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

// Models
import { IFaqBase } from '@/models/faqBase.model';

// Components
import { Modal, ModalSize, useForm } from '@/components';
import FaqForm from '../FaqForm/FaqForm';

// Helpers
import { mapToFormValues, TFaqFormValues } from '../../helpers';

type TEditFaqModalProps<T extends IFaqBase> = {
  isOpen: boolean;
  onClose: () => void;
  faq: T | null;
  onSubmit: (values: TFaqFormValues) => void;
  isLoading?: boolean;
};

const EditFaqModal = <T extends IFaqBase>({ isOpen, onClose, faq, onSubmit, isLoading }: TEditFaqModalProps<T>) => {
  const { t } = useTranslation();
  const [formInstance] = useForm();

  const initialValues = useMemo(() => (faq ? mapToFormValues(faq) : undefined), [faq]);

  useEffect(() => {
    if (!faq?.faqId) return;
    formInstance.setFieldsValue(mapToFormValues(faq));
  }, [formInstance, faq]);

  return (
    <Modal destroyOnClose title={t('edit_title_faq')} onCancel={onClose} visible={isOpen} size={ModalSize.LARGE}>
      <FaqForm
        formInstance={formInstance}
        submitText={t('edit')}
        onSubmit={onSubmit}
        onReset={onClose}
        isLoading={isLoading}
        initialValues={initialValues}
      />
    </Modal>
  );
};

export default EditFaqModal as FC<TEditFaqModalProps<IFaqBase>>;
